import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Vault,
  Plus,
  ArrowsLeftRight,
  Bank,
  Money,
  CaretDown,
  CaretUp,
  Warning,
  CheckCircle,
  Lock,
  Clock,
  Eye,
  Trash,
  ArrowRight,
  Receipt,
  Users,
  Storefront,
  Wallet,
  CurrencyDollar,
  CalendarBlank,
  ArrowsDownUp,
  ChartLineUp,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Para birimi formatlama
const formatCurrency = (amount, currency = 'TRY') => {
  if (amount === null || amount === undefined) return '-';
  const symbols = { TRY: '₺', EUR: '€', GBP: '£', USD: '$' };
  const symbol = symbols[currency] || currency;
  return `${symbol}${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Hareket tipi etiketleri
const movementTypeLabels = {
  transfer: 'Transfer',
  expense: 'Gider',
  income: 'Gelir',
  adjustment: 'Düzeltme',
};

const movementSubtypeLabels = {
  bank_deposit: 'Bankaya Yatırılan',
  main_cash_transfer: 'Ana Kasaya Aktarılan',
  supplier_payment: 'Tedarikçiye Ödeme',
  staff_advance: 'Personel Avansı',
  owner_withdrawal: 'İşletme Sahibine',
  expense_payment: 'Gider Ödemesi',
  cash_sale: 'Nakit Satış',
  other: 'Diğer',
};

// Kasa durumu badge'i
const StatusBadge = ({ status }) => {
  const config = {
    open: { label: 'Açık', className: 'bg-green-500/10 text-green-500 border-green-500/20' },
    closed: { label: 'Kapalı', className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
    locked: { label: 'Kilitli', className: 'bg-red-500/10 text-red-500 border-red-500/20' },
  };
  const { label, className } = config[status] || config.open;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${className}`}>
      {status === 'locked' && <Lock size={12} className="inline mr-1" />}
      {label}
    </span>
  );
};

export default function CashRegister() {
  const { user } = useAuth();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { language } = useLanguage();

  // State
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [movements, setMovements] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [staff, setStaff] = useState([]);

  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [activeTab, setActiveTab] = useState('daily'); // daily, accounts, movements

  // Modals
  const [showNewRegisterModal, setShowNewRegisterModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRegister, setSelectedRegister] = useState(null);

  // Form states
  const [newRegisterForm, setNewRegisterForm] = useState({
    account_id: '',
    date: new Date().toISOString().split('T')[0],
    opening_cash: '',
    opening_manual_reason: '',
    currency: 'TRY',
  });

  const [movementForm, setMovementForm] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_register_id: '',
    type: 'expense',
    subtype: 'expense_payment',
    source_account_id: '',
    destination_account_id: '',
    amount: '',
    target_type: '',
    target_entity_id: '',
    target_entity_name: '',
    description: '',
    currency: 'TRY',
  });

  const [closeForm, setCloseForm] = useState({
    actual_cash: '',
    difference_explanation: '',
    notes: '',
  });

  // Fetch functions
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cash-accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data);
      }
    } catch (error) {
      console.error('Hesaplar yüklenemedi:', error);
    }
  }, [token]);

  const fetchRegisters = useCallback(async () => {
    try {
      let url = `${API_URL}/api/cash-registers?start_date=${selectedDate}&end_date=${selectedDate}`;
      if (selectedAccountId) {
        url += `&account_id=${selectedAccountId}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRegisters(data);
      }
    } catch (error) {
      console.error('Kasa kayıtları yüklenemedi:', error);
    }
  }, [token, selectedDate, selectedAccountId]);

  const fetchMovements = useCallback(async () => {
    try {
      let url = `${API_URL}/api/cash-movements?start_date=${selectedDate}&end_date=${selectedDate}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMovements(data);
      }
    } catch (error) {
      console.error('Hareketler yüklenemedi:', error);
    }
  }, [token, selectedDate]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cash-dashboard?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Dashboard yüklenemedi:', error);
    }
  }, [token, selectedDate]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data);
      }
    } catch (error) {
      console.error('Tedarikçiler yüklenemedi:', error);
    }
  }, [token]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
      }
    } catch (error) {
      console.error('Personel yüklenemedi:', error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAccounts(),
        fetchRegisters(),
        fetchMovements(),
        fetchDashboard(),
        fetchSuppliers(),
        fetchStaff(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchAccounts, fetchRegisters, fetchMovements, fetchDashboard, fetchSuppliers, fetchStaff]);

  // Refetch when date changes
  useEffect(() => {
    fetchRegisters();
    fetchMovements();
    fetchDashboard();
  }, [selectedDate, selectedAccountId, fetchRegisters, fetchMovements, fetchDashboard]);

  // Handlers
  const handleCreateRegister = async () => {
    try {
      const payload = {
        date: newRegisterForm.date,
        account_id: newRegisterForm.account_id,
        currency: newRegisterForm.currency,
      };

      // Manuel açılış kasası (sadece admin)
      if (newRegisterForm.opening_cash && user?.role === 'admin') {
        payload.opening_cash = parseFloat(newRegisterForm.opening_cash);
        payload.opening_manual_reason = newRegisterForm.opening_manual_reason;
      }

      const res = await fetch(`${API_URL}/api/cash-registers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success('Kasa kaydı oluşturuldu');
        setShowNewRegisterModal(false);
        setNewRegisterForm({
          account_id: '',
          date: new Date().toISOString().split('T')[0],
          opening_cash: '',
          opening_manual_reason: '',
          currency: 'TRY',
        });
        fetchRegisters();
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Kasa oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleCreateMovement = async () => {
    try {
      const payload = {
        date: movementForm.date,
        cash_register_id: (movementForm.cash_register_id && movementForm.cash_register_id !== 'none') ? movementForm.cash_register_id : null,
        type: movementForm.type,
        subtype: movementForm.subtype,
        source_account_id: movementForm.source_account_id || null,
        destination_account_id: movementForm.destination_account_id || null,
        amount: parseFloat(movementForm.amount),
        target_type: movementForm.target_type || null,
        target_entity_id: movementForm.target_entity_id || null,
        target_entity_name: movementForm.target_entity_name || null,
        description: movementForm.description || null,
        currency: movementForm.currency,
      };

      const res = await fetch(`${API_URL}/api/cash-movements`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success('Para hareketi eklendi');
        setShowMovementModal(false);
        setMovementForm({
          date: new Date().toISOString().split('T')[0],
          cash_register_id: '',
          type: 'expense',
          subtype: 'expense_payment',
          source_account_id: '',
          destination_account_id: '',
          amount: '',
          target_type: '',
          target_entity_id: '',
          target_entity_name: '',
          description: '',
          currency: 'TRY',
        });
        fetchMovements();
        fetchRegisters();
        fetchDashboard();
        fetchAccounts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Hareket eklenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleCloseRegister = async () => {
    if (!selectedRegister) return;

    try {
      const payload = {
        actual_cash: parseFloat(closeForm.actual_cash),
        difference_explanation: closeForm.difference_explanation || null,
        notes: closeForm.notes || null,
      };

      const res = await fetch(`${API_URL}/api/cash-registers/${selectedRegister.id}/close`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success('Kasa kapatıldı');
        setShowCloseModal(false);
        setCloseForm({ actual_cash: '', difference_explanation: '', notes: '' });
        setSelectedRegister(null);
        fetchRegisters();
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Kasa kapatılamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleSyncSales = async (registerId) => {
    try {
      const res = await fetch(`${API_URL}/api/cash-registers/${registerId}/sync-sales`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Satışlar senkronize edildi');
        fetchRegisters();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Senkronizasyon başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleLockRegister = async (registerId) => {
    try {
      const res = await fetch(`${API_URL}/api/cash-registers/${registerId}/lock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Kasa kilitlendi');
        fetchRegisters();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Kilitleme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDeleteMovement = async (movementId) => {
    if (!window.confirm('Bu hareketi silmek istediğinizden emin misiniz?')) return;

    try {
      const res = await fetch(`${API_URL}/api/cash-movements/${movementId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Hareket silindi');
        fetchMovements();
        fetchRegisters();
        fetchDashboard();
        fetchAccounts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Silme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Permission checks
  const canManageRegister = user?.role === 'admin' || user?.role === 'manager';
  const canLock = user?.role === 'admin';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="cash-register-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Vault size={28} weight="duotone" className="text-primary" />
              {language === 'tr' ? 'Kasa Yönetimi' : 'Cash Register'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'tr' ? 'Günlük kasa, para hareketleri ve hesap bakiyeleri' : 'Daily cash, movements and account balances'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
              data-testid="date-filter"
            />
            {canManageRegister && (
              <>
                <Button onClick={() => setShowNewRegisterModal(true)} data-testid="new-register-btn">
                  <Plus size={18} className="mr-1" />
                  {language === 'tr' ? 'Kasa Aç' : 'Open Register'}
                </Button>
                <Button variant="outline" onClick={() => setShowMovementModal(true)} data-testid="new-movement-btn">
                  <ArrowsLeftRight size={18} className="mr-1" />
                  {language === 'tr' ? 'Hareket Ekle' : 'Add Movement'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Dashboard Summary Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-summary">
            <div className="bg-card border rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Money size={18} />
                {language === 'tr' ? 'Toplam Nakit' : 'Total Cash'}
              </div>
              <div className="text-2xl font-bold mt-1 text-green-500" data-testid="total-cash">
                {formatCurrency(dashboard.total_cash_balance)}
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Bank size={18} />
                {language === 'tr' ? 'Banka' : 'Bank'}
              </div>
              <div className="text-2xl font-bold mt-1 text-blue-500" data-testid="total-bank">
                {formatCurrency(dashboard.total_bank_balance)}
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <ChartLineUp size={18} />
                {language === 'tr' ? 'Günlük Giriş' : 'Daily In'}
              </div>
              <div className="text-2xl font-bold mt-1 text-emerald-500" data-testid="daily-inflows">
                {formatCurrency(dashboard.movement_summary?.total_inflows || 0)}
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <ArrowsDownUp size={18} />
                {language === 'tr' ? 'Günlük Çıkış' : 'Daily Out'}
              </div>
              <div className="text-2xl font-bold mt-1 text-red-500" data-testid="daily-outflows">
                {formatCurrency(dashboard.movement_summary?.total_outflows || 0)}
              </div>
            </div>
          </div>
        )}

        {/* Bugünkü Satışlar (daily_sales'den) - Kasa açılmadan önce de görünür */}
        {dashboard?.daily_sales && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4" data-testid="daily-sales-info">
            <div className="flex items-center gap-2 mb-3">
              <Receipt size={20} className="text-emerald-600" />
              <span className="font-medium text-emerald-800">
                {language === 'tr' ? 'Bugünkü Satışlar (Kar/Zarar\'dan)' : 'Today\'s Sales (from P&L)'}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-emerald-600">Nakit</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="daily-sales-cash">
                  {formatCurrency(dashboard.daily_sales.cash)}
                </p>
              </div>
              <div>
                <p className="text-xs text-blue-600">Kart</p>
                <p className="text-lg font-bold text-blue-700" data-testid="daily-sales-card">
                  {formatCurrency(dashboard.daily_sales.card)}
                </p>
              </div>
              <div>
                <p className="text-xs text-purple-600">Online</p>
                <p className="text-lg font-bold text-purple-700" data-testid="daily-sales-online">
                  {formatCurrency(dashboard.daily_sales.online)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Toplam</p>
                <p className="text-lg font-bold text-gray-800" data-testid="daily-sales-total">
                  {formatCurrency(dashboard.daily_sales.total)}
                </p>
              </div>
            </div>
            <p className="text-xs text-emerald-600 mt-2">
              {language === 'tr' 
                ? 'Kasa açıldığında nakit satış otomatik olarak kasaya yansıyacak.' 
                : 'Cash sales will automatically sync when register is opened.'}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('daily')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'daily'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-daily"
          >
            <CalendarBlank size={16} className="inline mr-1" />
            {language === 'tr' ? 'Günlük Kasa' : 'Daily Register'}
          </button>
          <button
            onClick={() => setActiveTab('accounts')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'accounts'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-accounts"
          >
            <Wallet size={16} className="inline mr-1" />
            {language === 'tr' ? 'Hesap Bakiyeleri' : 'Account Balances'}
          </button>
          <button
            onClick={() => setActiveTab('movements')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'movements'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-movements"
          >
            <ArrowsLeftRight size={16} className="inline mr-1" />
            {language === 'tr' ? 'Para Hareketleri' : 'Movements'}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'daily' && (
          <div className="space-y-4" data-testid="daily-registers-section">
            {registers.length === 0 ? (
              <div className="text-center py-12 bg-card border rounded-lg">
                <Vault size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {language === 'tr' ? 'Bu tarih için kasa kaydı yok' : 'No register for this date'}
                </p>
                {canManageRegister && (
                  <Button className="mt-4" onClick={() => setShowNewRegisterModal(true)}>
                    <Plus size={18} className="mr-1" />
                    {language === 'tr' ? 'Kasa Aç' : 'Open Register'}
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {registers.map((reg) => (
                  <div
                    key={reg.id}
                    className="bg-card border rounded-lg p-4"
                    data-testid={`register-card-${reg.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{reg.account_name}</h3>
                          <StatusBadge status={reg.status} />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {reg.date} | {reg.currency}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {reg.status === 'open' && canManageRegister && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSyncSales(reg.id)}
                              data-testid={`sync-btn-${reg.id}`}
                            >
                              <ArrowsDownUp size={16} className="mr-1" />
                              Satış Senkronize
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedRegister(reg);
                                setCloseForm({
                                  actual_cash: '',
                                  difference_explanation: '',
                                  notes: '',
                                });
                                setShowCloseModal(true);
                              }}
                              data-testid={`close-btn-${reg.id}`}
                            >
                              <CheckCircle size={16} className="mr-1" />
                              Kapat
                            </Button>
                          </>
                        )}
                        {reg.status === 'closed' && canLock && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleLockRegister(reg.id)}
                            data-testid={`lock-btn-${reg.id}`}
                          >
                            <Lock size={16} className="mr-1" />
                            Kilitle
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedRegister(reg);
                            setShowDetailModal(true);
                          }}
                          data-testid={`detail-btn-${reg.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </div>
                    </div>

                    {/* Register Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Açılış</p>
                        <p className="font-medium" data-testid={`opening-${reg.id}`}>
                          {formatCurrency(reg.opening_cash, reg.currency)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {reg.opening_source === 'manual' ? '(Manuel)' : '(Devir)'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Nakit Satış</p>
                        <p className="font-medium text-green-500" data-testid={`cash-sales-${reg.id}`}>
                          {formatCurrency(reg.sales?.cash || 0, reg.currency)}
                        </p>
                        {reg.sales?.auto_synced && (
                          <p className="text-xs text-muted-foreground">
                            <CheckCircle size={10} className="inline" /> Senkronize
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Diğer Girişler</p>
                        <p className="font-medium text-emerald-500" data-testid={`inflows-${reg.id}`}>
                          {formatCurrency(reg.total_inflows || 0, reg.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Çıkışlar</p>
                        <p className="font-medium text-red-500" data-testid={`outflows-${reg.id}`}>
                          {formatCurrency(reg.total_outflows || 0, reg.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Beklenen Kasa</p>
                        <p className="font-medium" data-testid={`expected-${reg.id}`}>
                          {formatCurrency(reg.expected_cash || 0, reg.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fiili / Fark</p>
                        {reg.actual_cash !== null ? (
                          <>
                            <p className="font-medium" data-testid={`actual-${reg.id}`}>
                              {formatCurrency(reg.actual_cash, reg.currency)}
                            </p>
                            <p
                              className={`text-xs font-medium ${
                                reg.difference_alert ? 'text-red-500' : 'text-green-500'
                              }`}
                              data-testid={`difference-${reg.id}`}
                            >
                              {reg.difference_alert && <Warning size={10} className="inline mr-1" />}
                              Fark: {formatCurrency(reg.difference, reg.currency)}
                            </p>
                          </>
                        ) : (
                          <p className="text-muted-foreground">-</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="space-y-4" data-testid="accounts-section">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {accounts.map((acc) => (
                <div
                  key={acc.id}
                  className="bg-card border rounded-lg p-4"
                  data-testid={`account-card-${acc.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {acc.type === 'cash' && <Money size={24} className="text-green-500" />}
                    {acc.type === 'bank' && <Bank size={24} className="text-blue-500" />}
                    {acc.type === 'pos' && <CurrencyDollar size={24} className="text-purple-500" />}
                    {acc.type === 'other' && <Wallet size={24} className="text-gray-500" />}
                    <div>
                      <h3 className="font-semibold">{acc.name}</h3>
                      <p className="text-xs text-muted-foreground">{acc.code}</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold" data-testid={`balance-${acc.id}`}>
                    {formatCurrency(acc.current_balance, acc.currency)}
                  </div>
                  {acc.bank_name && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {acc.bank_name} {acc.bank_account_no && `- ${acc.bank_account_no}`}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'movements' && (
          <div className="space-y-4" data-testid="movements-section">
            {movements.length === 0 ? (
              <div className="text-center py-12 bg-card border rounded-lg">
                <ArrowsLeftRight size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {language === 'tr' ? 'Bu tarih için hareket yok' : 'No movements for this date'}
                </p>
              </div>
            ) : (
              <div className="bg-card border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Tip</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Kaynak</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Hedef</th>
                      <th className="px-4 py-3 text-right text-sm font-medium">Tutar</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Açıklama</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Oluşturan</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {movements.map((mov) => (
                      <tr key={mov.id} data-testid={`movement-row-${mov.id}`}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium">
                            {movementSubtypeLabels[mov.subtype] || mov.subtype}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {movementTypeLabels[mov.type] || mov.type}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {mov.source_account_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {mov.destination_account_name || mov.target_entity_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span
                            className={`font-medium ${
                              mov.direction === 'in'
                                ? 'text-green-500'
                                : mov.direction === 'out'
                                ? 'text-red-500'
                                : 'text-blue-500'
                            }`}
                          >
                            {mov.direction === 'in' ? '+' : mov.direction === 'out' ? '-' : ''}
                            {formatCurrency(mov.amount, mov.currency)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                          {mov.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {mov.created_by_name}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {canLock && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => handleDeleteMovement(mov.id)}
                              data-testid={`delete-movement-${mov.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* New Register Modal */}
        <Dialog open={showNewRegisterModal} onOpenChange={setShowNewRegisterModal}>
          <DialogContent data-testid="new-register-modal">
            <DialogHeader>
              <DialogTitle>
                {language === 'tr' ? 'Yeni Kasa Aç' : 'Open New Register'}
              </DialogTitle>
              <DialogDescription>
                {language === 'tr'
                  ? 'Seçilen hesap ve tarih için günlük kasa kaydı oluşturun'
                  : 'Create daily register for selected account and date'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Hesap</Label>
                <Select
                  value={newRegisterForm.account_id}
                  onValueChange={(v) => setNewRegisterForm({ ...newRegisterForm, account_id: v })}
                >
                  <SelectTrigger data-testid="register-account-select">
                    <SelectValue placeholder="Hesap seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts
                      .filter((a) => a.type === 'cash')
                      .map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={newRegisterForm.date}
                  onChange={(e) => setNewRegisterForm({ ...newRegisterForm, date: e.target.value })}
                  data-testid="register-date-input"
                />
              </div>

              {user?.role === 'admin' && (
                <>
                  <div>
                    <Label>Manuel Açılış Kasası (Opsiyonel)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Devir yerine manuel tutar"
                      value={newRegisterForm.opening_cash}
                      onChange={(e) =>
                        setNewRegisterForm({ ...newRegisterForm, opening_cash: e.target.value })
                      }
                      data-testid="register-opening-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Boş bırakırsanız önceki günün kapanışından otomatik devir yapılır
                    </p>
                  </div>

                  {newRegisterForm.opening_cash && (
                    <div>
                      <Label>Manuel Açılış Gerekçesi *</Label>
                      <Textarea
                        placeholder="Manuel açılış için gerekçe yazın"
                        value={newRegisterForm.opening_manual_reason}
                        onChange={(e) =>
                          setNewRegisterForm({
                            ...newRegisterForm,
                            opening_manual_reason: e.target.value,
                          })
                        }
                        data-testid="register-reason-input"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewRegisterModal(false)}>
                İptal
              </Button>
              <Button
                onClick={handleCreateRegister}
                disabled={
                  !newRegisterForm.account_id ||
                  !newRegisterForm.date ||
                  (newRegisterForm.opening_cash && !newRegisterForm.opening_manual_reason)
                }
                data-testid="create-register-btn"
              >
                Kasa Aç
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Movement Modal */}
        <Dialog open={showMovementModal} onOpenChange={setShowMovementModal}>
          <DialogContent className="max-w-lg" data-testid="movement-modal">
            <DialogHeader>
              <DialogTitle>
                {language === 'tr' ? 'Para Hareketi Ekle' : 'Add Cash Movement'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={movementForm.date}
                    onChange={(e) => setMovementForm({ ...movementForm, date: e.target.value })}
                    data-testid="movement-date-input"
                  />
                </div>
                <div>
                  <Label>Tip</Label>
                  <Select
                    value={movementForm.type}
                    onValueChange={(v) => setMovementForm({ ...movementForm, type: v })}
                  >
                    <SelectTrigger data-testid="movement-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Gider Çıkışı</SelectItem>
                      <SelectItem value="transfer">Hesaplar Arası Transfer</SelectItem>
                      <SelectItem value="income">Gelir</SelectItem>
                      <SelectItem value="adjustment">Düzeltme</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Alt Tip</Label>
                <Select
                  value={movementForm.subtype}
                  onValueChange={(v) => setMovementForm({ ...movementForm, subtype: v })}
                >
                  <SelectTrigger data-testid="movement-subtype-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_deposit">Bankaya Yatırılan</SelectItem>
                    <SelectItem value="main_cash_transfer">Ana Kasaya Aktarılan</SelectItem>
                    <SelectItem value="supplier_payment">Tedarikçiye Ödeme</SelectItem>
                    <SelectItem value="staff_advance">Personel Avansı</SelectItem>
                    <SelectItem value="owner_withdrawal">İşletme Sahibine</SelectItem>
                    <SelectItem value="expense_payment">Gider Ödemesi</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Kaynak Hesap</Label>
                <Select
                  value={movementForm.source_account_id}
                  onValueChange={(v) => setMovementForm({ ...movementForm, source_account_id: v })}
                >
                  <SelectTrigger data-testid="movement-source-select">
                    <SelectValue placeholder="Kaynak hesap seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name} ({formatCurrency(acc.current_balance, acc.currency)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(movementForm.type === 'transfer' || movementForm.type === 'income') && (
                <div>
                  <Label>Hedef Hesap</Label>
                  <Select
                    value={movementForm.destination_account_id}
                    onValueChange={(v) =>
                      setMovementForm({ ...movementForm, destination_account_id: v })
                    }
                  >
                    <SelectTrigger data-testid="movement-dest-select">
                      <SelectValue placeholder="Hedef hesap seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts
                        .filter((a) => a.id !== movementForm.source_account_id)
                        .map((acc) => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(movementForm.subtype === 'supplier_payment' ||
                movementForm.subtype === 'staff_advance') && (
                <div>
                  <Label>
                    {movementForm.subtype === 'supplier_payment' ? 'Tedarikçi' : 'Personel'}
                  </Label>
                  <Select
                    value={movementForm.target_entity_id}
                    onValueChange={(v) => {
                      const list =
                        movementForm.subtype === 'supplier_payment' ? suppliers : staff;
                      const entity = list.find((e) => e.id === v);
                      setMovementForm({
                        ...movementForm,
                        target_entity_id: v,
                        target_entity_name: entity?.name || '',
                        target_type:
                          movementForm.subtype === 'supplier_payment' ? 'supplier' : 'staff',
                      });
                    }}
                  >
                    <SelectTrigger data-testid="movement-entity-select">
                      <SelectValue placeholder="Seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {(movementForm.subtype === 'supplier_payment' ? suppliers : staff).map(
                        (entity) => (
                          <SelectItem key={entity.id} value={entity.id}>
                            {entity.name}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Tutar</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={movementForm.amount}
                  onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })}
                  data-testid="movement-amount-input"
                />
              </div>

              <div>
                <Label>İlişkili Kasa Kaydı (Opsiyonel)</Label>
                <Select
                  value={movementForm.cash_register_id}
                  onValueChange={(v) => setMovementForm({ ...movementForm, cash_register_id: v })}
                >
                  <SelectTrigger data-testid="movement-register-select">
                    <SelectValue placeholder="Kasa kaydı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seçilmedi</SelectItem>
                    {registers
                      .filter((r) => r.status !== 'locked')
                      .map((reg) => (
                        <SelectItem key={reg.id} value={reg.id}>
                          {reg.account_name} - {reg.date}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Açıklama</Label>
                <Textarea
                  placeholder="Hareket açıklaması"
                  value={movementForm.description}
                  onChange={(e) => setMovementForm({ ...movementForm, description: e.target.value })}
                  data-testid="movement-description-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMovementModal(false)}>
                İptal
              </Button>
              <Button
                onClick={handleCreateMovement}
                disabled={!movementForm.amount || parseFloat(movementForm.amount) <= 0}
                data-testid="create-movement-btn"
              >
                Ekle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Close Register Modal */}
        <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
          <DialogContent data-testid="close-register-modal">
            <DialogHeader>
              <DialogTitle>Kasa Kapat</DialogTitle>
              <DialogDescription>
                {selectedRegister?.account_name} - {selectedRegister?.date}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">Beklenen Kasa:</span>
                  <span className="font-bold text-lg">
                    {formatCurrency(selectedRegister?.expected_cash || 0, selectedRegister?.currency)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Açılış ({formatCurrency(selectedRegister?.opening_cash || 0)}) + Nakit Satış (
                  {formatCurrency(selectedRegister?.sales?.cash || 0)}) + Girişler (
                  {formatCurrency(selectedRegister?.total_inflows || 0)}) - Çıkışlar (
                  {formatCurrency(selectedRegister?.total_outflows || 0)})
                </p>
              </div>

              <div>
                <Label>Fiili Kasa (Sayım) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Kasadaki gerçek tutar"
                  value={closeForm.actual_cash}
                  onChange={(e) => setCloseForm({ ...closeForm, actual_cash: e.target.value })}
                  data-testid="close-actual-input"
                />
              </div>

              {closeForm.actual_cash && (
                <div
                  className={`p-3 rounded-lg ${
                    Math.abs(
                      parseFloat(closeForm.actual_cash) - (selectedRegister?.expected_cash || 0)
                    ) > 50
                      ? 'bg-red-500/10 border border-red-500/20'
                      : 'bg-green-500/10 border border-green-500/20'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Kasa Farkı:</span>
                    <span
                      className={`font-bold ${
                        Math.abs(
                          parseFloat(closeForm.actual_cash) - (selectedRegister?.expected_cash || 0)
                        ) > 50
                          ? 'text-red-500'
                          : 'text-green-500'
                      }`}
                      data-testid="close-difference"
                    >
                      {formatCurrency(
                        parseFloat(closeForm.actual_cash) - (selectedRegister?.expected_cash || 0),
                        selectedRegister?.currency
                      )}
                    </span>
                  </div>
                </div>
              )}

              {closeForm.actual_cash &&
                Math.abs(
                  parseFloat(closeForm.actual_cash) - (selectedRegister?.expected_cash || 0)
                ) > 50 && (
                  <div>
                    <Label>Fark Açıklaması *</Label>
                    <Textarea
                      placeholder="Kasa farkının nedeni"
                      value={closeForm.difference_explanation}
                      onChange={(e) =>
                        setCloseForm({ ...closeForm, difference_explanation: e.target.value })
                      }
                      data-testid="close-explanation-input"
                    />
                  </div>
                )}

              <div>
                <Label>Notlar (Opsiyonel)</Label>
                <Textarea
                  placeholder="Ek notlar"
                  value={closeForm.notes}
                  onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloseModal(false)}>
                İptal
              </Button>
              <Button
                onClick={handleCloseRegister}
                disabled={
                  !closeForm.actual_cash ||
                  (Math.abs(
                    parseFloat(closeForm.actual_cash) - (selectedRegister?.expected_cash || 0)
                  ) > 50 &&
                    !closeForm.difference_explanation)
                }
                data-testid="confirm-close-btn"
              >
                Kasayı Kapat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl" data-testid="detail-modal">
            <DialogHeader>
              <DialogTitle>
                Kasa Detayı - {selectedRegister?.account_name}
              </DialogTitle>
              <DialogDescription>
                {selectedRegister?.date} | <StatusBadge status={selectedRegister?.status} />
              </DialogDescription>
            </DialogHeader>

            {selectedRegister && (
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                {/* Açılış Bilgisi */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Clock size={16} /> Açılış
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Açılış Kasası:</span>
                      <span className="ml-2 font-medium">
                        {formatCurrency(selectedRegister.opening_cash, selectedRegister.currency)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Kaynak:</span>
                      <span className="ml-2">
                        {selectedRegister.opening_source === 'manual' ? 'Manuel' : 'Otomatik Devir'}
                      </span>
                    </div>
                    {selectedRegister.opening_manual_reason && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Gerekçe:</span>
                        <span className="ml-2">{selectedRegister.opening_manual_reason}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Satış Bilgisi */}
                <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2 text-green-600">
                    <Receipt size={16} /> Satışlar (daily_sales)
                  </h4>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Nakit:</span>
                      <div className="font-medium text-green-500">
                        {formatCurrency(selectedRegister.sales?.cash || 0)}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Kart:</span>
                      <div className="font-medium">
                        {formatCurrency(selectedRegister.sales?.card || 0)}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Online:</span>
                      <div className="font-medium">
                        {formatCurrency(selectedRegister.sales?.online || 0)}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Toplam:</span>
                      <div className="font-medium">
                        {formatCurrency(selectedRegister.sales?.total || 0)}
                      </div>
                    </div>
                  </div>
                  {selectedRegister.sales?.auto_synced && (
                    <p className="text-xs text-green-600 mt-2">
                      <CheckCircle size={12} className="inline mr-1" />
                      Otomatik senkronize edildi
                    </p>
                  )}
                </div>

                {/* Hareket Özeti */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-4">
                    <h4 className="font-medium mb-2 text-emerald-600">Girişler</h4>
                    <div className="text-2xl font-bold text-emerald-500">
                      {formatCurrency(selectedRegister.total_inflows || 0)}
                    </div>
                    {selectedRegister.inflow_details &&
                      Object.entries(selectedRegister.inflow_details).map(([key, val]) => (
                        <div key={key} className="text-xs text-muted-foreground mt-1">
                          {movementSubtypeLabels[key] || key}: {formatCurrency(val)}
                        </div>
                      ))}
                  </div>
                  <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-4">
                    <h4 className="font-medium mb-2 text-red-600">Çıkışlar</h4>
                    <div className="text-2xl font-bold text-red-500">
                      {formatCurrency(selectedRegister.total_outflows || 0)}
                    </div>
                    {selectedRegister.outflow_details &&
                      Object.entries(selectedRegister.outflow_details).map(([key, val]) => (
                        <div key={key} className="text-xs text-muted-foreground mt-1">
                          {movementSubtypeLabels[key] || key}: {formatCurrency(val)}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Kapanış Bilgisi */}
                {selectedRegister.status !== 'open' && (
                  <div className="bg-muted/30 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Kapanış</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Beklenen:</span>
                        <div className="font-medium">
                          {formatCurrency(selectedRegister.expected_cash)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Fiili:</span>
                        <div className="font-medium">
                          {formatCurrency(selectedRegister.actual_cash)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Fark:</span>
                        <div
                          className={`font-medium ${
                            selectedRegister.difference_alert ? 'text-red-500' : 'text-green-500'
                          }`}
                        >
                          {formatCurrency(selectedRegister.difference)}
                        </div>
                      </div>
                    </div>
                    {selectedRegister.difference_explanation && (
                      <div className="mt-2 text-sm">
                        <span className="text-muted-foreground">Fark Açıklaması:</span>
                        <p>{selectedRegister.difference_explanation}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Devir */}
                {selectedRegister.carry_forward?.amount > 0 && (
                  <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-4">
                    <h4 className="font-medium mb-2 text-blue-600">Sonraki Güne Devir</h4>
                    <div className="text-xl font-bold">
                      {formatCurrency(selectedRegister.carry_forward.amount)}
                    </div>
                  </div>
                )}

                {/* Kilit Bilgisi */}
                {selectedRegister.status === 'locked' && (
                  <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-4">
                    <h4 className="font-medium mb-2 text-red-600 flex items-center gap-2">
                      <Lock size={16} /> Kilitli
                    </h4>
                    <div className="text-sm">
                      <p>
                        <span className="text-muted-foreground">Kilitleyen:</span>{' '}
                        {selectedRegister.locked_by_name}
                      </p>
                      <p>
                        <span className="text-muted-foreground">Tarih:</span>{' '}
                        {selectedRegister.locked_at}
                      </p>
                      {selectedRegister.lock_reason && (
                        <p>
                          <span className="text-muted-foreground">Gerekçe:</span>{' '}
                          {selectedRegister.lock_reason}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Kapat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
